import {
  CoverageFeeProductMatchType,
  isCoverageFeeProduct,
} from "@redotech/redo-return-coverage-common/is-coverage-fee-product";
import { compareMaybeGids } from "@redotech/redo-shopify-common/gid-utils";
import { DiscountDistributionMethod } from "./exchange-settings";
import {
  getOriginProductTotals,
  MaybeOriginOrderArray,
  OriginOrder,
} from "./origin-order-utils";
import type {
  ReturnTotalsLineItem,
  ReturnTotalsOrder,
  ReturnTotalsShopifyOrder,
} from "./return-order-contracts";
import { LogFn, LogLevel } from "./return-totals-logging";
import { THIRD_PARTY_SOURCE } from "./third-party";

interface ReturnTotalsOriginLookupOrder {
  shopify: Pick<ReturnTotalsShopifyOrder, "id" | "source_name">;
}

export function getTaxForLineItem(lineItem: ReturnTotalsLineItem | undefined) {
  let taxValue = 0.0;
  const taxLines = lineItem?.tax_lines || [];
  for (const taxLine of taxLines) {
    taxValue += parseFloat(taxLine.price);
  }
  taxValue /= lineItem?.quantity || 1;

  return Math.round(taxValue * 100) / 100;
}

export function getTaxForBundledPart(
  bundleLineItem: any,
  percentOfBundle: number,
) {
  return getTaxForLineItem(bundleLineItem) * percentOfBundle;
}
function getSimpleBundleStackID(
  lineItem: ReturnTotalsLineItem,
  order: ReturnTotalsOrder,
): number | null {
  if (!lineItem.discount_allocations) {
    return null;
  }

  let bundleLineItemID: string | null = null;
  const re = new RegExp(/^Simple Bundles:/);
  const line_id_regex = new RegExp(/ID: \d+$/);

  for (const allocation of lineItem.discount_allocations) {
    const application =
      order.shopify.discount_applications[
        allocation.discount_application_index
      ];

    const title = application?.title;
    if (title && re.test(title)) {
      bundleLineItemID =
        title.match(line_id_regex)?.[0]?.match(/\d+/)?.[0] ?? null;
    }
  }

  return Number(bundleLineItemID);
}

function getSimpleBundleItemDiscount(
  lineItem: ReturnTotalsLineItem,
  order: ReturnTotalsOrder,
  originBundleID: number,
): number {
  let bundledItemsTotalPriceValue = 0;
  const itemPrice = +lineItem.price * lineItem.quantity;

  for (const item of order.shopify.line_items) {
    const bundleID = getSimpleBundleStackID(item, order);

    if (originBundleID === bundleID) {
      bundledItemsTotalPriceValue += Number(item.price) * item.quantity;
    }
  }

  const itemProportion = itemPrice / bundledItemsTotalPriceValue;
  const bundleItem = order.shopify.line_items.find(
    (item) => item.id === originBundleID,
  );
  if (!bundleItem) {
    return 0;
  }

  const bundleDiscounts =
    bundleItem.discount_allocations?.reduce(
      (acc: number, discount: any) => (acc += Number(discount.amount)),
      0,
    ) || 0;
  const bundlePrice =
    Number(bundleItem.price) * Number(bundleItem.quantity) - bundleDiscounts;

  const proportionalPrice = bundlePrice * itemProportion;
  const finalDiscount = itemPrice - proportionalPrice;

  return finalDiscount;
}

function isLineItemCanceled(lineItem: ReturnTotalsLineItem) {
  return (
    lineItem.fulfillment_status === null && lineItem.fulfillable_quantity === 0
  );
}

export function getDiscountAllocations(
  lineItem: ReturnTotalsLineItem | undefined,
  order: ReturnTotalsOrder,
  distributionMethod: DiscountDistributionMethod = DiscountDistributionMethod.LINE_ITEM,
  redoCoverageProductIds: ReadonlySet<string>,
) {
  if (!lineItem) {
    return 0;
  }

  const discountAllocations = [...lineItem.discount_allocations];

  // Check to see if item is part of a 'simple bundle'
  // If so calculate its discount
  const originBundleID = getSimpleBundleStackID(lineItem, order);
  if (originBundleID) {
    return getSimpleBundleItemDiscount(lineItem, order, originBundleID);
  }

  for (const item of order.shopify.line_items) {
    if (isLineItemCanceled(item)) {
      continue;
    }
    for (const discount of item.discount_allocations) {
      const targetSelection =
        order.shopify.discount_applications[discount.discount_application_index]
          ?.target_selection ?? "";

      if (
        !isRedoItem(item, redoCoverageProductIds) &&
        !discountAllocations.some(
          (allocation: any) =>
            allocation.discount_application_index ===
            discount.discount_application_index,
        ) &&
        (distributionMethod === DiscountDistributionMethod.ORDER ||
          discount.discount_application_index == undefined) &&
        ["entitled", "explicit"].includes(targetSelection)
      ) {
        discountAllocations.push(discount);
      }
    }
  }

  if (!discountAllocations?.length) {
    return 0;
  }

  let discountAmount = 0;
  for (const discount of discountAllocations) {
    // Ignore redo discounts as we want the return value to include
    // discounts from previous returns (i.e. customers should be
    // able to return items that were previously returned)

    // Discounts are distributed differently according to team settings
    let totalAmountDiscountedFromOrder = 0;
    let totalValueOfDiscountedItems = 0;

    if (distributionMethod === DiscountDistributionMethod.LINE_ITEM) {
      totalAmountDiscountedFromOrder = +discount.amount;
      totalValueOfDiscountedItems = +lineItem?.price * lineItem.quantity;
    } else {
      const acrossOrder =
        distributionMethod === DiscountDistributionMethod.ORDER;
      for (const item of order.shopify.line_items) {
        if (isLineItemCanceled(item)) {
          continue;
        }
        if (!isRedoItem(item, redoCoverageProductIds)) {
          totalValueOfDiscountedItems += acrossOrder
            ? +item.price * item.quantity
            : 0;
          for (const allocation of item.discount_allocations) {
            if (
              allocation.discount_application_index ===
              discount.discount_application_index
            ) {
              totalAmountDiscountedFromOrder += +allocation.amount;
              totalValueOfDiscountedItems += acrossOrder
                ? 0
                : +item.price * item.quantity;
            }
          }
        }
      }
    }

    const discount_code =
      order.shopify.discount_applications[discount.discount_application_index]
        ?.code;
    if (
      !(
        discount_code?.substring(0, 4) === "REDO" &&
        discount_code?.length === 19
      ) &&
      totalValueOfDiscountedItems > 0
    ) {
      discountAmount +=
        totalAmountDiscountedFromOrder *
        ((+lineItem?.price * lineItem.quantity) / totalValueOfDiscountedItems);
    }
  }

  return discountAmount;
}

export function isRedoItem(
  lineItem: ReturnTotalsLineItem | undefined,
  redoCoverageProductIds: ReadonlySet<string>,
) {
  if (!lineItem) {
    return false;
  }
  return isCoverageFeeProduct({
    product: {
      vendor: lineItem.vendor,
      sku: lineItem.sku,
      title: lineItem.title,
      product_id: lineItem.product_id?.toString(),
    },
    redoCoverageProductIds,
    matchType: CoverageFeeProductMatchType.REDO_AND_COMPETITORS,
  });
}

export function getTaxRateForLineItem(lineItem: ReturnTotalsLineItem) {
  return (lineItem?.tax_lines || []).reduce((total, taxLine) => {
    return total + (taxLine?.rate ?? 0);
  }, 0);
}
function getOriginItemPrice(
  lineItem: ReturnTotalsLineItem | undefined,
  order: ReturnTotalsOrder | undefined,
  originOrders: MaybeOriginOrderArray,
  logFn: LogFn,
) {
  if (order?.shopify.source_name === THIRD_PARTY_SOURCE) {
    return null;
  }

  try {
    const originOrder = getOriginOrderForLineItem(lineItem, originOrders);
    if (originOrder && lineItem && !("error" in originOrder)) {
      const originProductTotals = getOriginProductTotals(
        lineItem.id,
        lineItem.variant_id,
        originOrder,
      );
      if ("error" in originProductTotals) {
        logFn(
          LogLevel.WARN,
          `ORIGIN ORDERS: Error getting product totals for origin item price.
          This can happen if the merchant manually edited the exchange order.`,
          { error: originProductTotals.error, orderId: order?.shopify.id },
        );
      } else {
        return originProductTotals.price;
      }
    } else if (!lineItem) {
      logFn(LogLevel.ERROR, "ORIGIN ORDERS: No line item found", {
        orderId: order?.shopify.id,
      });
    }
  } catch (e) {
    logFn(
      LogLevel.ERROR,
      `ORIGIN ORDERS: Error getting origin item price: ${e}`,
      { orderId: order?.shopify.id, lineItemId: lineItem?.id },
    );
  }

  return null;
}

function getOriginOrderForLineItem(
  lineItem: ReturnTotalsLineItem | undefined,
  originOrders: MaybeOriginOrderArray,
): OriginOrder | undefined {
  return originOrders.find(
    (maybeOriginOrder): maybeOriginOrder is OriginOrder =>
      maybeOriginOrder &&
      !("error" in maybeOriginOrder) &&
      maybeOriginOrder.lineItems.some(
        (li) => lineItem && compareMaybeGids(lineItem.id, li.id),
      ),
  );
}

function getOriginItemDiscountPrice(
  lineItem: ReturnTotalsLineItem | undefined,
  order: ReturnTotalsOrder | undefined,
  originOrders: MaybeOriginOrderArray,
  logFn: LogFn,
) {
  if (order?.shopify.source_name === THIRD_PARTY_SOURCE) {
    return null;
  }

  try {
    const originOrder = getOriginOrderForLineItem(lineItem, originOrders);
    if (lineItem && originOrder && !("error" in originOrder)) {
      const originProductTotals = getOriginProductTotals(
        lineItem.id,
        lineItem.variant_id,
        originOrder,
      );
      if ("error" in originProductTotals) {
        logFn(
          LogLevel.WARN,
          `ORIGIN ORDERS: Error getting product totals for origin item discount price.
          This can happen if the merchant manually edited the exchange order.`,
          { error: originProductTotals.error, orderId: order?.shopify.id },
        );
      } else {
        return originProductTotals.discountPrice;
      }
    } else if (!lineItem) {
      logFn(LogLevel.ERROR, "ORIGIN ORDERS: No line item found", {
        orderId: order?.shopify.id,
      });
    }
  } catch (e) {
    logFn(
      LogLevel.ERROR,
      `ORIGIN ORDERS: Error getting origin item discount price: ${e}`,
      { orderId: order?.shopify.id, lineItemId: lineItem?.id },
    );
  }

  return null;
}

export function getOriginItemTax(
  lineItem: ReturnTotalsLineItem | undefined,
  order: ReturnTotalsOriginLookupOrder | undefined,
  originOrders: MaybeOriginOrderArray,
  logFn: LogFn,
) {
  if (order?.shopify.source_name === THIRD_PARTY_SOURCE) {
    return null;
  }

  try {
    const originOrder = getOriginOrderForLineItem(lineItem, originOrders);
    if (lineItem && originOrder && !("error" in originOrder)) {
      const originProductTotals = getOriginProductTotals(
        lineItem.id,
        lineItem.variant_id,
        originOrder,
      );
      if ("error" in originProductTotals) {
        logFn(
          LogLevel.WARN,
          `ORIGIN ORDERS: Error getting product totals for origin item tax.
          This can happen if the merchant manually edited the exchange order.`,
          { error: originProductTotals.error, orderId: order?.shopify.id },
        );
      } else {
        return originProductTotals.tax;
      }
    } else if (!lineItem) {
      logFn(LogLevel.ERROR, "ORIGIN ORDERS: No line item found", {
        orderId: order?.shopify.id,
      });
    }
  } catch (e) {
    logFn(
      LogLevel.ERROR,
      `ORIGIN ORDERS: Error getting origin item tax: ${e}`,
      { orderId: order?.shopify.id, lineItemId: lineItem?.id },
    );
  }

  return null;
}
export function getDiscountPriceNew({
  lineItem,
  order,
  originOrders,
  logFn,
  distributionMethod = DiscountDistributionMethod.LINE_ITEM,
  percentOfBundle = 1,
  redoCoverageProductIds,
}: {
  lineItem: ReturnTotalsLineItem;
  order: ReturnTotalsOrder;
  originOrders: MaybeOriginOrderArray;
  logFn: LogFn;
  distributionMethod?: DiscountDistributionMethod;
  percentOfBundle?: number;
  redoCoverageProductIds: ReadonlySet<string>;
}) {
  let discountPrice = 0;
  const originPrice = getOriginItemDiscountPrice(
    lineItem,
    order,
    originOrders,
    logFn,
  );
  if (order.isExchangeOrder && originPrice) {
    discountPrice = Number(originPrice);
  } else {
    const quantity = lineItem.originalQuantity || lineItem.quantity || 1;
    const totalLineItemDiscount =
      getDiscountAllocations(
        lineItem,
        order,
        distributionMethod,
        redoCoverageProductIds,
      ) / quantity;
    discountPrice = parseFloat(lineItem.price) - totalLineItemDiscount;
  }

  return (discountPrice * percentOfBundle).toFixed(2);
}

export function getPreDiscountPrice({
  lineItem,
  order,
  originOrders,
  logFn,
  percentOfBundle = 1,
}: {
  lineItem: ReturnTotalsLineItem;
  order: ReturnTotalsOrder;
  originOrders: MaybeOriginOrderArray;
  logFn: LogFn;
  percentOfBundle: number;
}) {
  const originPrice = getOriginItemPrice(lineItem, order, originOrders, logFn);
  const preDiscountPrice =
    originPrice && order.isExchangeOrder
      ? parseFloat(originPrice)
      : parseFloat(lineItem.price);

  return (preDiscountPrice * percentOfBundle).toFixed(2);
}

/**
 * @deprecated Use getDiscountPriceNew instead
 */
export function getDiscountPriceOld(
  lineItem: ReturnTotalsLineItem | undefined,
  order: ReturnTotalsOrder,
  originOrders: MaybeOriginOrderArray,
  logFn: LogFn,
  distributionMethod: DiscountDistributionMethod | undefined,
  product: any,
  redoCoverageProductIds: ReadonlySet<string>,
) {
  const originPrice = getOriginItemDiscountPrice(
    lineItem,
    order,
    originOrders,
    logFn,
  );
  if (lineItem && product?.isUnbundled) {
    if (originPrice && order.isExchangeOrder) {
      return (Number(originPrice) * (product.percentOfBundle ?? 1)).toFixed(2);
    }

    const totalLineItemDiscount =
      getDiscountAllocations(
        lineItem,
        order,
        distributionMethod,
        redoCoverageProductIds,
      ) / (lineItem.originalQuantity || lineItem.quantity);
    const percentOfBundle =
      parseFloat(product.price) / parseFloat(lineItem.price);

    const proportionalPrice = product.percentOfBundle
      ? parseFloat(lineItem.price) * product.percentOfBundle
      : parseFloat(product.price);

    return (
      proportionalPrice -
      totalLineItemDiscount * (product.percentOfBundle || percentOfBundle)
    ).toFixed(2);
  }
  if (originPrice && order.isExchangeOrder) {
    return originPrice;
  }

  const discountAmount = getDiscountAllocations(
    lineItem,
    order,
    distributionMethod,
    redoCoverageProductIds,
  );

  return (
    Number(lineItem?.price || 0) -
    discountAmount / (lineItem?.quantity || 1)
  ).toFixed(2);
}
